/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { CurrentUserService } from '../authentication/current-user.service';
import { environment } from '../../../../environments/environment';
import { AppUtils } from '../../../shared/utils/app.utils';
import { firstValueFrom, Observable } from 'rxjs';
import { ISensor } from '../../interfaces/sensor';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ICreateSwitchRequest } from '../../interfaces/create-switch-request';
import { IParkoutCommand } from '../../interfaces/parkout-command';
import { IRequestBody } from '@app/core/interfaces/switch';

@Injectable({
  providedIn: 'root',
})
export class SiteControlApi {
  private readonly http = inject(HttpClient);

  private readonly currentUser = inject(CurrentUserService);

  /**
   * Panics start
   * @param roverList
   * @returns
   */
  public panicStart(roverList: string[], shipyardName: string, shipyardId: string) {
    return this.http.post(
      `${environment.API_SITE_CONTROLING_URL}/RoverCommands/emergencystop?shipyardName=${shipyardName}&shipyardId=${shipyardId}`,
      roverList,
    );
  }

  notifyPanicMode(panicMode: boolean, shipyardName: string, shipyardId: string) {
    return this.http.post(
      `${environment.API_SITE_CONTROLING_URL}/RoverCommands/notifypanicmode?panicMode=${panicMode}&shipyardName=${shipyardName}&shipyardId=${shipyardId}`,
      null,
    );
  }

  switch(reqBody: ICreateSwitchRequest) {
    const body = this.prepareRequestBody(reqBody);
    return this.http.post(`${environment.API_SITE_CONTROLING_URL}/AutoSwitch/assignswitch`, body, {
      responseType: 'text',
    });
  }

  parkout(parkoutCommand: IParkoutCommand) {
    const body = parkoutCommand;
    return firstValueFrom(
      this.http.post(`${environment.API_SITE_CONTROLING_URL}/RoverCommands/parkout`, body),
    );
  }

  prepareRequestBody(
    createSwitchReq: ICreateSwitchRequest,
    reqId?: string,
    source?: string,
  ): IRequestBody {
    const req: IRequestBody = {
      header: {
        requestId: reqId ?? AppUtils.getUuid(),
        requestTimestamp: new Date().toISOString(),
        source: source ?? 'HubPilot',
        target: 'HubPilot',
      },
      data: [
        {
          ...createSwitchReq,
        },
      ],
    };
    return req;
  }

  /**
   * To create a switch operation
   */
  async createSwitch(createSwitchReq: ICreateSwitchRequest, reqId?: string, source?: string) {
    const body = this.prepareRequestBody(createSwitchReq, reqId, source);
    const emptyTrailer = createSwitchReq.isEmptyTrailer;
    const headers = new HttpHeaders().set('emptyTrailer', String(emptyTrailer));
    const result = await firstValueFrom(
      this.http.post(`${environment.API_SITE_CONTROLING_URL}/AutoSwitch/assignswitch`, body, {
        responseType: 'text',
        headers,
      }),
    );
    return result;
  }

  panicStatusChangeNotification(shipyardName: string) {
    return this.http.get(
      `${environment.API_SITE_CONTROLING_URL}/RoverCommands/getpanicmode?shipyardName=${shipyardName}`,
    );
  }

  resetMRM(roverList: string[], shipyardName: string, shipyardId: string) {
    return this.http.post<any>(
      `${environment.API_SITE_CONTROLING_URL}/RoverCommands/resetmrm?shipyardName=${shipyardName}&shipyardId=${shipyardId}`,
      roverList,
    );
  }

  getSensors(shipyardName: string): Observable<any[]> {
    const URL = `${this.currentUser.getUserBaseSiteMonitoringUrl}/DynamicStatus/sensors?shipyardName=${shipyardName}`;
    return this.http.get<any>(URL);
  }

  /**
   * To get list of sensors from API
   */
  getDeviceSensors(shipyardName: string): Observable<ISensor[]> {
    const URL = `${environment.API_ANALYTIC_SERVICE_URL}/DeviceDiagnostics/getSensors?shipyardName=${shipyardName}`;
    return this.http.get<any>(URL);
  }

  /**
   * Call shutdown API call
   * @param hardwareId
   * @param resetRequired
   * @param shipyardId
   */
  shutdownreset(hardwareId: string, resetRequired: boolean, shipyardId: string) {
    return this.http.post(
      `${environment.API_SITE_CONTROLING_URL}/PowerCommands/shutdown?hardwareId=${hardwareId}&resetRequired=${resetRequired}&shipyardId=${shipyardId}`,
      null,
      { responseType: 'text' },
    );
  }

  /**
   * Call launch API call
   * @param targetId
   * @param shipyardId
   * @param targetState
   */
  launch(targetId: string, shipyardId: string, targetState?: string) {
    const operationType = `${targetState === undefined ? '' : `&operationState=${targetState}`}`;
    return this.http.post(
      `${environment.API_SITE_CONTROLING_URL}/PowerCommands/launch?targetId=${targetId}&shipyardId=${shipyardId}${operationType}`,
      null,
      { responseType: 'text' },
    );
  }

  /**
   * Switch abandon api
   * @param uuid
   */
  switchAbandon(uuid: string) {
    return this.http.get<any>(
      `${environment.API_SITE_CONTROLING_URL}/SwitchStatus/switchabandon?uuid=${uuid}`,
    );
  }

  /**
   * Switch abandon api
   * @param uuid
   */
  assignSwitch(body: IRequestBody, headers: HttpHeaders) {
    return this.http.post(`${environment.API_SITE_CONTROLING_URL}/AutoSwitch/assignswitch`, body, {
      responseType: 'text',
      headers,
    });
  }

  fetchSwitches(shipyardId: string) {
    return this.http.get<any>(
      `${environment.API_SITE_CONTROLING_URL}/SwitchStatus/switches?shipyardId=${shipyardId}`,
    );
  }

  fetchRecentSwitchesHistory(shipyardName: string, shipyardId: string) {
    return this.http.get<any>(
      `${environment.API_SITE_CONTROLING_URL}/SwitchStatus/recentswitches?shipyardName=${shipyardName}&shipyardId=${shipyardId}`,
    );
  }
}
